"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { type Dispatch, type SetStateAction, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/locales/client";
import { useCreateFolder } from "../api/use-create-folder";
import { useEditFolder } from "../api/use-edit-folder";

const formSchema = z.object({
  name: z.string().min(1, {
    message: "Required",
  }),
});

interface EditStudySetModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  folderId?: string; // Optional for create mode
  folderName?: string; // Optional for create mode
  mode: "create" | "edit";
}

const FolderForm = ({
  folderId,
  folderName,
  isOpen,
  setIsOpen,
  mode,
}: EditStudySetModalProps) => {
  const t = useI18n();
  const editFolder = useEditFolder();
  const createFolder = useCreateFolder();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: folderName || "",
    },
  });

  useEffect(() => {
    if (mode === "edit") {
      form.setValue("name", folderName || "");
    }
  }, [form, folderName, mode]);

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    if (mode === "edit" && folderId) {
      editFolder.mutate(
        {
          id: folderId,
          name: values.name,
        },
        {
          onSuccess: () => {
            setIsOpen(false);
          },
        },
      );
    } else {
      createFolder.mutate(
        {
          name: values.name,
        },
        {
          onSuccess: () => {
            setIsOpen(false);
          },
        },
      );
    }
  };

  const isLoading =
    mode === "edit" ? editFolder.isPending : createFolder.isPending;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          name="name"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("folderName")}</FormLabel>
              <FormControl>
                <Input
                  onKeyDown={(e) => e.stopPropagation()}
                  {...field}
                  placeholder={t("folderNamePlaceholder")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end w-full space-x-2">
          <Button
            className="w-full md:w-auto"
            type="submit"
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isLoading
              ? t("saving")
              : mode === "edit"
                ? t("save")
                : t("create")}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default FolderForm;
